import type { ChargeView, TenantAddonView } from '@/api/generated';
import { moneyIn } from '@/components/moneyIn';
import { formatCivilRange } from '@/i18n/format';
import i18n from '@/i18n/i18n';

function tr(key: string, options?: Record<string, string | number>): string {
  return options ? i18n.t(key, options) : i18n.t(key);
}

export function countOfferedAddons(addons: TenantAddonView[]): number {
  return addons.filter((addon) => addon.status === 'OFFERED').length;
}

/** Active and ending at term end is its own registry status; the label stays `addons.endsAtTermEnd`. */
export function addonStatusKey(addon: TenantAddonView): string | undefined {
  if (addon.status === 'ACTIVE' && addon.cancelAtTermEnd) return 'ACTIVE_CANCEL_AT_TERM_END';
  return addon.status;
}

export function priceLabel(addon: TenantAddonView, locale: string, currency: string): string {
  const unavailable = tr('addons.unavailable');
  const money = moneyIn(addon.priceMinor, currency, locale, unavailable);
  if (money === unavailable) return unavailable;
  if (addon.period === 'ANNUAL') return tr('addons.priceWithPeriod', { price: money, period: tr('addons.period.annual') });
  if (addon.period === 'MONTHLY') {
    return tr('addons.priceWithPeriod', { price: money, period: tr('addons.period.monthly') });
  }
  return money;
}

function civilRange(start: string | undefined, end: string | undefined, locale: string): string {
  const dash = tr('addons.unavailable');
  return formatCivilRange(start, end, locale, {
    openEnded: (from) => tr('common.dateFrom', { date: from }),
    endOnly: (to) => tr('common.dateUntil', { date: to }),
    dash,
  });
}

export function termLabel(addon: TenantAddonView, locale: string): string {
  const range = civilRange(addon.termStart, addon.termEnd, locale);
  if (addon.autoRenew && !addon.cancelAtTermEnd && range !== tr('addons.unavailable')) {
    return tr('addons.termWithRenews', { term: range, renews: tr('addons.renews') });
  }
  return range;
}

export function billingLabel(addon: TenantAddonView): string {
  if (addon.maskedCard) return addon.maskedCard;
  if (addon.billingMode === 'NET_FROM_PAYOUTS') return tr('addons.billing.netted');
  return tr('addons.unavailable');
}

export function chargeHowLabel(charge: ChargeView): string {
  if (charge.mode === 'NETTED') return tr('addons.charge.netted');
  if (charge.maskedCard) return charge.maskedCard;
  return tr('addons.unavailable');
}

export function chargeWhenLabel(charge: ChargeView, locale: string): string {
  return civilRange(charge.periodStart, charge.periodEnd, locale);
}

export function allowedBillingModes(
  allowed: TenantAddonView['allowedBilling'],
): Array<'CARD' | 'NET_FROM_PAYOUTS'> {
  if (allowed === 'CARD') return ['CARD'];
  if (allowed === 'NET_FROM_PAYOUTS') return ['NET_FROM_PAYOUTS'];
  return ['CARD', 'NET_FROM_PAYOUTS'];
}

export function defaultBillingMode(
  allowed: TenantAddonView['allowedBilling'],
  lastChoice: 'CARD' | 'NET_FROM_PAYOUTS' | null,
): 'CARD' | 'NET_FROM_PAYOUTS' {
  const modes = allowedBillingModes(allowed);
  if (lastChoice && modes.includes(lastChoice)) return lastChoice;
  return modes[0] ?? 'CARD';
}
